import { useEffect, useState } from "react";
import { getDefaultConfigs } from "@utils/api-communication/RestApiCli";
import { ManagementConfig, ProductConfig } from "types/productManagementTypes";
import { ProductFetchStatus } from "./DeviceStatesPanel";

// Mirrors product.service getDefaultConfigForProduct: hardwareModel is the pid
// prefix before "-", puiVersion its 3-part prefix, version an exact match.

export interface VersionInfo {
  hardwareModel: string;
  puiVersion: string;
  version: string;
}

export type DefaultConfigState =
  | { status: "loading" }
  | { status: "error"; msg: string }
  | { status: "noVersionInfo" }
  | { status: "noMatch"; versionInfo: VersionInfo }
  | { status: "match"; versionInfo: VersionInfo; defaultConfig: ProductConfig };

export const resolveMatch = (
  pid: string,
  product: { puiVersion?: string; version?: string } | null,
  defaults: ProductConfig[],
): DefaultConfigState => {
  if (!product?.puiVersion || !product?.version) {
    return { status: "noVersionInfo" };
  }
  const versionInfo: VersionInfo = {
    hardwareModel: pid.includes("-") ? pid.split("-")[0] : pid,
    puiVersion: product.puiVersion.split(".").slice(0, 3).join("."),
    version: product.version,
  };
  const defaultConfig = defaults.find(
    (d) =>
      d.hardwareModel === versionInfo.hardwareModel &&
      d.puiVersion === versionInfo.puiVersion &&
      d.version === versionInfo.version,
  );
  return defaultConfig
    ? { status: "match", versionInfo, defaultConfig }
    : { status: "noMatch", versionInfo };
};

export const applicationStatus = (
  deviceConfig: ProductConfig,
): { color: string; label: string; detail: string } => {
  if (deviceConfig.type === "apply" && deviceConfig.defaultConfigSaved) {
    return deviceConfig.applied
      ? {
          color: "green",
          label: "Applied",
          detail: "This device's config was saved from this default and the device confirmed it.",
        }
      : {
          color: "yellow",
          label: "Pending",
          detail:
            "This default is saved as the device's config and will be pushed when the device next connects.",
        };
  }
  return {
    color: "orange",
    label: "To be applied",
    detail:
      "The device's current config was not saved from this default. On the next connect, the backend saves this default for the device and pushes it.",
  };
};

export const versionInfoLine = (v: VersionInfo) =>
  `${v.hardwareModel} / PUI ${v.puiVersion} / app ${v.version}`;

// Fields an admin can set from the config form; the update-source fields are
// left out because a default config never carries a device-specific download.
const COMPARED_FIELDS: (keyof ManagementConfig)[] = [
  "brightness",
  "ipd",
  "trackingSettings",
  "playBoundary",
  "usbDebugging",
  "systemUpdateOn",
  "wifiAlwaysOn",
  "setAppAsHome",
  "appAutoUpdate",
  "sleepDelayTime",
  "screenOffDelayTime",
  "idleDelayTime",
  "idleShutdownTime",
  "checktimeout",
];

/**
 * Lists the settings where the device's own config differs from the default
 * config it matches, so the UI can say whether the applied config is still the
 * default or has been customized for this device.
 */
export const diffAgainstDefault = (
  applied?: Partial<ManagementConfig> | null,
  defaultConfig?: Partial<ManagementConfig> | null,
): (keyof ManagementConfig)[] => {
  if (!applied || !defaultConfig) return [];
  return COMPARED_FIELDS.filter((field) => {
    const a = applied[field];
    const b = defaultConfig[field];
    if (a === undefined || b === undefined) return false;
    return a !== b;
  });
};

type ProductVersions = { puiVersion?: string; version?: string };

export const useDeviceDefaultConfig = (
  pid: string,
  product: ProductVersions | null,
  productStatus: ProductFetchStatus,
): DefaultConfigState => {
  const [state, setState] = useState<DefaultConfigState>({ status: "loading" });

  useEffect(() => {
    let cancelled = false;
    if (productStatus === "loading") {
      setState({ status: "loading" });
      return;
    }
    if (productStatus === "error") {
      setState({ status: "error", msg: "Failed to fetch the device's version info" });
      return;
    }
    setState({ status: "loading" });
    getDefaultConfigs(
      (defaultsResponse) => {
        if (cancelled) return;
        const defaults: ProductConfig[] = Array.isArray(defaultsResponse?.data)
          ? defaultsResponse.data
          : [];
        setState(resolveMatch(pid, product, defaults));
      },
      () => {
        if (!cancelled) {
          setState({ status: "error", msg: "Failed to fetch default configs" });
        }
      },
    );
    return () => {
      cancelled = true;
    };
  }, [pid, product, productStatus]);

  return state;
};
